import { useQuery, useQueryClient } from '@tanstack/react-query'
import { LayoutDashboard, Plus } from 'lucide-react'
import { useState } from 'react'
import { useLocation } from 'wouter'
import { api } from '../lib/api'
import { ago } from '../lib/format'
import type { PageBlock } from '../lib/types'
import { useWorkspace } from './Shell'
import { Loading, Modal, Spinner, useToast } from './ui'

/** Pin something to a page (yours, or a new one): used from views, statements and agent answers. */
export function PinToPage({ block, what, onClose }: { block: PageBlock; what: string; onClose: () => void }) {
  const { wid } = useWorkspace()
  const qc = useQueryClient()
  const toast = useToast()
  const [, navigate] = useLocation()
  const q = useQuery({
    queryKey: ['ws', wid, 'pages'],
    queryFn: () => api.get<{ pages: { id: string; title: string; mine: boolean; updatedAt: string }[] }>(`/w/${wid}/pages`),
  })
  const mine = (q.data?.pages ?? []).filter(p => p.mine)
  const [picked, setTarget] = useState<string | null>(null)
  // your most recently changed page, until you choose another (or a new one)
  const target = picked ?? mine[0]?.id ?? '__new'
  const [title, setTitle] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const pin = async () => {
    setBusy(true)
    setError(null)
    try {
      let id = target
      if (target === '__new') {
        const p = await api.post<{ id: string }>(`/w/${wid}/pages`, {
          spec: { version: 1, title: title.trim() || 'My page', blocks: [block] },
        })
        id = p.id
      } else await api.post(`/w/${wid}/pages/${id}/blocks`, { block })
      await qc.invalidateQueries({ queryKey: ['ws', wid, 'pages'] })
      await qc.invalidateQueries({ queryKey: ['ws', wid, 'page', id] })
      onClose()
      toast('Pinned', `${what} is on the page now`, <LayoutDashboard size={15} />)
      navigate(`/pages/${id}`)
    } catch (e) {
      setError((e as Error).message)
    } finally {
      setBusy(false)
    }
  }
  return (
    <Modal
      title="Pin to page"
      sub={`${what} stays live on the page: it recomputes from the plan every time you open it.`}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="btn primary"
            onClick={pin}
            disabled={busy || (target === '__new' && !title.trim() && mine.length > 0)}
          >
            {busy ? <Spinner size={12} /> : <LayoutDashboard size={13} />} Pin
          </button>
        </>
      }
    >
      <div className="stack">
        {q.isLoading ? (
          <Loading rows={3} />
        ) : (
          <div className="pin-list">
            {mine.map(p => (
              <label key={p.id} className={`pin-option ${target === p.id ? 'on' : ''}`}>
                <input type="radio" name="pin" checked={target === p.id} onChange={() => setTarget(p.id)} />
                <LayoutDashboard size={14} />
                <span className="grow">{p.title}</span>
                <span className="tiny faint">updated {ago(p.updatedAt)}</span>
              </label>
            ))}
            <label className={`pin-option ${target === '__new' ? 'on' : ''}`}>
              <input type="radio" name="pin" checked={target === '__new'} onChange={() => setTarget('__new')} />
              <Plus size={14} />
              <input
                type="text"
                className="input bare grow"
                placeholder="A new page called…"
                value={title}
                onFocus={() => setTarget('__new')}
                onChange={e => setTitle(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && title.trim() && !busy && void pin()}
              />
            </label>
          </div>
        )}
        {error && <div className="issue">{error}</div>}
      </div>
    </Modal>
  )
}
